import { Link } from "react-router-dom";
import {
  Package, Wrench, PartyPopper, Bus, UtensilsCrossed, Plane, Hotel, UserPlus,
  ArrowRight, IndianRupee, PiggyBank, FileCheck, Receipt, Truck, Printer,
} from "lucide-react";
import Layout from "@/components/Layout";

const CARDS = [
  {
    slug: "material", title: "Material Requisition", ref: "VGWS-MAT-2026",
    icon: Package, tone: "maroon",
    blurb: "Stationery, hardware, software — anything the school needs to buy.",
    tat: "Submit at least 3 working days prior.",
  },
  {
    slug: "service", title: "Service Requisition", ref: "VGWS-SRV-2026",
    icon: Wrench, tone: "purple",
    blurb: "AMCs, repairs, consultancies, vendor callouts — services only, no material purchase.",
    tat: "Submit at least 3 working days prior.",
  },
  {
    slug: "event", title: "Event Requisition", ref: "VGWS-EVT-2026",
    icon: PartyPopper, tone: "gold",
    blurb: "Auditorium, stage, backdrop, sound, IT, seating, food, media — one sheet for the whole event.",
    tat: "Submit at least 21 working days prior.",
  },
  {
    slug: "transportation", title: "Transportation Requisition", ref: "VGWS-TPT-2026",
    icon: Bus, tone: "teal",
    blurb: "Bus, car or taxi for students, staff or guests — one-way or round trip.",
    tat: "Submit at least 2 working days prior.",
  },
  {
    slug: "fnb", title: "Cake / Food & Beverages Requisition", ref: "VGWS-FNB-2026",
    icon: UtensilsCrossed, tone: "saffron",
    blurb: "Refreshments, meals, cakes and cutlery — for staff, guests or programme days.",
    tat: "Submit at least 2 working days prior.",
  },
  {
    slug: "travel", title: "Travel Tickets Requisition", ref: "VGWS-TVL-2026",
    icon: Plane, tone: "teal-dark",
    blurb: "Flights, trains, buses or taxis for students, staff or invited guests.",
    tat: "Submit at least 10 working days prior.",
  },
  {
    slug: "hotel", title: "Hotel Booking Requisition", ref: "VGWS-HTL-2026",
    icon: Hotel, tone: "maroon-dark",
    blurb: "Room stays for staff / guests / senior management — with taxi & meal preferences.",
    tat: "Submit at least 7 working days prior.",
  },
  {
    slug: "guest", title: "Guest Visit - Stay in Staff Quarters Form", ref: "VGWS-GST-2026",
    icon: UserPlus, tone: "saffron-dark",
    blurb: "Register overnight guest(s) on the school campus — with ID proof, vehicle and stay details.",
    tat: "Submit at least 1 working day prior.",
  },
  {
    slug: "advance_requisition", title: "Advance Requisition", ref: "VGWS-ADV-2026",
    icon: IndianRupee, tone: "maroon",
    blurb: "Request a cash advance for an upcoming school activity or trip.",
    tat: "Submit at least 3 working days prior.",
  },
  {
    slug: "pocket_money", title: "Pocket Money Requisition", ref: "VGWS-PKT-2026",
    icon: PiggyBank, tone: "saffron",
    blurb: "House-parent request to withdraw a boarding student's imprest for a specific need.",
    tat: "Submit at least 2 working days prior.",
  },
  {
    slug: "advance_settlement", title: "Advance Settlement", ref: "VGWS-STL-2026",
    icon: FileCheck, tone: "teal",
    blurb: "Settle an earlier advance — attach every bill and record the balance refunded or excess.",
    tat: "Submit within 5 working days of return.",
  },
  {
    slug: "reimbursement", title: "Reimbursement", ref: "VGWS-RMB-2026",
    icon: Receipt, tone: "teal-dark",
    blurb: "Employee reimbursement for local travel, D.A., food and stay expenses.",
    tat: "Submit within 5 working days of expense.",
  },
  {
    slug: "maini", title: "Internal Maini Booking", ref: "VGWS-MNB-2026",
    icon: Truck, tone: "teal",
    blurb: "Book the internal Maini utility vehicle for on-campus material movement or short transport.",
    tat: "CAO approves; Process Owner (Transport) fulfils.",
  },
  {
    slug: "print_job", title: "Print Job Requisition", ref: "VGWS-PNT-2026",
    icon: Printer, tone: "purple",
    blurb: "Class handouts, brochures, reports or event collateral — per-row PDF uploads, auto cost by page.",
    tat: "HOD approves; Print PO fulfils. HOD may update until fulfilment.",
  },
];

const TONE = {
  maroon:      { bar: "bg-[#7a1f2b]", ring: "hover:ring-[#7a1f2b]", chip: "bg-[#f4e9eb] text-[#7a1f2b]" },
  "maroon-dark":{ bar: "bg-[#5a1520]", ring: "hover:ring-[#5a1520]", chip: "bg-[#f2e6e8] text-[#5a1520]" },
  teal:        { bar: "bg-[#2d6b6b]", ring: "hover:ring-[#2d6b6b]", chip: "bg-[#e8f2f2] text-[#2d6b6b]" },
  "teal-dark": { bar: "bg-[#1c4a4a]", ring: "hover:ring-[#1c4a4a]", chip: "bg-[#dceaea] text-[#1c4a4a]" },
  gold:        { bar: "bg-[#b48a45]", ring: "hover:ring-[#b48a45]", chip: "bg-[#f9f2e0] text-[#8a6a2f]" },
  saffron:     { bar: "bg-[#c8632a]", ring: "hover:ring-[#c8632a]", chip: "bg-[#faeadd] text-[#a04f22]" },
  "saffron-dark":{ bar: "bg-[#8a3f19]", ring: "hover:ring-[#8a3f19]", chip: "bg-[#f5e3d5] text-[#8a3f19]" },
  purple:      { bar: "bg-[#6b3fa0]", ring: "hover:ring-[#6b3fa0]", chip: "bg-[#efe5f8] text-[#6b3fa0]" },
};

export default function NewRequisitionSelector() {
  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-4 text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">
          Start a new requisition
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight">
          Which sheet do you need today?
        </h1>
        <p className="text-sm text-[#4a524c] mt-1 max-w-2xl">
          Pick the requisition type below. Every sheet routes through the same
          seven-stop approval flow with a full audit trail.
        </p>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="form-selector-grid">
          {CARDS.map((c) => {
            const Icon = c.icon;
            const tone = TONE[c.tone];
            return (
              <Link
                key={c.slug}
                to={`/requisitions/new/${c.slug}`}
                className={`sheet p-6 relative overflow-hidden group hover:ring-2 ${tone.ring} transition-all hover-lift`}
                data-testid={`form-card-${c.slug}`}
              >
                <div className={`absolute left-0 top-0 h-full w-1.5 ${tone.bar}`} />
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-11 h-11 rounded-lg ${tone.bar} text-white flex items-center justify-center shadow-sm`}>
                    <Icon size={22} />
                  </div>
                  <div className={`text-[10px] uppercase tracking-widest ${tone.chip} px-2 py-1 rounded-full font-semibold`}>
                    {c.ref}
                  </div>
                </div>
                <h3 className="font-serif text-xl font-bold text-[#1f2320]">{c.title}</h3>
                <p className="text-sm text-[#4a524c] mt-2 leading-relaxed min-h-[3.5em]">{c.blurb}</p>
                <div className="mt-4 pt-4 border-t border-[#f2ecdf] flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-widest text-[#6e7770]">{c.tat}</span>
                  <ArrowRight size={16} className="text-[#7a1f2b] group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
